import {Parameter} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {RecordInformationBody} from '@dms/api/core';
import {RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {SnippetProps} from '@dms/shared';

import {Nullish, buildArray, useNavigate} from 'shared/web';

import {hasRecordInformation} from '../../utils/hasRecordInformation';
import {
  RecordAuditPairResourceId,
  getRecordAuditPairResourceLabel,
  isRecordAuditPairResourceId,
} from '../recordAuditPairResources';
import {PairedRecordSnippetTitle} from './PairedRecordSnippetTitle';
import {PairedRecordView} from './PairedRecordView';
import {useBusinessCasePairedRecord} from './useBusinessCasePairedRecord';
import {useInterestPairedRecord} from './useInterestPairedRecord';
import {useRentalOrderPairedRecord} from './useRentalOrderPairedRecord';
import {useServiceCasePairedRecord} from './useServiceCasePairedRecord';
import {useServiceOrderPairedRecord} from './useServiceOrderPairedRecord';
import {useVehicleSnippet} from './useVehicleSnippet';
import {useWorkshopInterestPairedRecord} from './useWorkshopInterestPairedRecord';

export interface PairedRecordSnippetArgs {
  record: RecordIdHttpModel | null;
  recordInformation: RecordInformationBody | null;
  isEditable: boolean;
  onPair: () => void;
}

export interface PairedRecordSnippetResult {
  /** Main paired-entity snippet (or a "+ pair" placeholder when nothing is paired). */
  mainSnippet: SnippetProps;
  /** Vehicle snippet shown next to a non-vehicle record; null when the record IS a vehicle or has none. */
  vehicleSnippet: SnippetProps | null;
  /** Cover photo of the linked vehicle, drives the header image. */
  coverImageUrl: string | Nullish;
  /** True while any linked record/vehicle is loading — drives EntityHeaderV2's snippet skeletons. */
  isLoading: boolean;
}

const MAIN_TEST_ID = 'recordAuditDetail-header-recordSnippet';

const idFor = (
  record: RecordIdHttpModel | null,
  resourceId: RecordAuditPairResourceId
): string | null => (record?.resourceId === resourceId ? record.id : null);

export function usePairedRecordSnippet(args: PairedRecordSnippetArgs): PairedRecordSnippetResult {
  const navigate = useNavigate();
  const {record} = args;
  const recordInformation = hasRecordInformation(args.recordInformation)
    ? args.recordInformation
    : null;
  const resourceId = isRecordAuditPairResourceId(record?.resourceId) ? record.resourceId : null;

  const businessCase = useBusinessCasePairedRecord(idFor(record, 'BUSINESS_CASE'));
  const serviceCase = useServiceCasePairedRecord(idFor(record, 'SERVICE_CASE'));
  const serviceOrder = useServiceOrderPairedRecord(idFor(record, 'SERVICE_ORDER'));
  const rentalOrder = useRentalOrderPairedRecord(idFor(record, 'RENTAL_ORDER'));
  const interest = useInterestPairedRecord(idFor(record, 'INTEREST'));
  const workshopInterest = useWorkshopInterestPairedRecord(idFor(record, 'WORKSHOP_INTEREST'));

  const activeView: PairedRecordView | null = match(resourceId)
    .with('BUSINESS_CASE', () => businessCase)
    .with('SERVICE_CASE', () => serviceCase)
    .with('SERVICE_ORDER', () => serviceOrder)
    .with('RENTAL_ORDER', () => rentalOrder)
    .with('INTEREST', () => interest)
    .with('WORKSHOP_INTEREST', () => workshopInterest)
    .otherwise(() => null);

  // VEHICLE record -> the vehicle itself is the main entity; otherwise the linked vehicle.
  const primaryVehicleId =
    resourceId === 'VEHICLE' ? (record?.id ?? null) : (activeView?.vehicleIds[0] ?? null);
  const vehicle = useVehicleSnippet(primaryVehicleId);

  const placeholderSnippet: SnippetProps = {
    label: i18n.t('page.checklistAudits.pairing.dialogTitle'),
    placeholder: {
      title: i18n.t('page.checklistAudits.pairing.cta'),
      onClick: args.isEditable ? args.onPair : undefined,
    },
    'data-testid': MAIN_TEST_ID,
  };

  const baseMainSnippet = resolveMainSnippet({
    resourceId,
    record,
    recordInformation,
    activeView,
    vehicleSnippet: vehicle.snippet,
    placeholderSnippet,
    onOpenDetail: (path: string) => navigate(path),
  });

  // Wrap the title so the code links to detail and (while editable) edit/unpair buttons sit alongside it.
  const mainSnippet: SnippetProps = isNotNil(baseMainSnippet.title)
    ? {
        ...baseMainSnippet,
        title: (
          <PairedRecordSnippetTitle
            title={baseMainSnippet.title}
            onOpenDetail={baseMainSnippet.onClick}
            onEdit={args.isEditable ? args.onPair : undefined}
            data-testid={MAIN_TEST_ID}
          />
        ),
        onClick: undefined,
      }
    : baseMainSnippet;

  const isLoading = (activeView?.isLoading ?? false) || (vehicle.snippet?.isLoading ?? false);

  return {
    mainSnippet,
    vehicleSnippet: resourceId === 'VEHICLE' ? null : vehicle.snippet,
    coverImageUrl: vehicle.coverImageUrl,
    isLoading,
  };
}

interface ResolveMainSnippetArgs {
  resourceId: RecordAuditPairResourceId | null;
  record: RecordIdHttpModel | null;
  recordInformation: RecordInformationBody | null;
  activeView: PairedRecordView | null;
  vehicleSnippet: SnippetProps | null;
  placeholderSnippet: SnippetProps;
  onOpenDetail: (path: string) => void;
}

const resolveMainSnippet = (args: ResolveMainSnippetArgs): SnippetProps => {
  if (args.resourceId === 'VEHICLE') {
    return args.vehicleSnippet ?? args.placeholderSnippet;
  }

  if (isNotNil(args.resourceId) && isNotNil(args.activeView)) {
    const view = args.activeView;
    const detailPath = view.detailPath;
    return {
      label: getRecordAuditPairResourceLabel(args.resourceId),
      title: view.code,
      onClick: isNotNil(detailPath) ? () => args.onOpenDetail(detailPath) : undefined,
      parameters: view.parameters,
      isLoading: view.isLoading,
      'data-testid': MAIN_TEST_ID,
    };
  }

  if (isNotNil(args.recordInformation)) {
    return buildCustomInfoSnippet(args.recordInformation);
  }

  // Paired to a resource without a dedicated snippet yet (e.g. CUSTOMER).
  if (isNotNil(args.record)) {
    return {
      label: args.record.resourceId,
      title: args.record.id,
      'data-testid': MAIN_TEST_ID,
    };
  }

  return args.placeholderSnippet;
};

const buildCustomInfoSnippet = (info: RecordInformationBody): SnippetProps => {
  const parameters = buildArray<Parameter>()
    .when(isNotNil(info.vin), `${i18n.t('page.checklistAudits.create.fields.vin')}: ${info.vin}`)
    .when(
      isNotNil(info.orderNumber),
      `${i18n.t('page.checklistAudits.create.fields.orderNumber')}: ${info.orderNumber}`
    );

  const title = info.regPlate ?? info.description ?? info.vin ?? info.orderNumber;

  return {
    label: i18n.t('page.checklistAudits.pairing.tabs.custom'),
    title: isNil(title) ? undefined : title,
    parameters,
    'data-testid': MAIN_TEST_ID,
  };
};
